import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import {
  ChatMessagesService,
  MessageModel,
  MessagesSearchModel,
} from '@seahorse/domain';
import { ResultStatus } from '@seahorse/common';
import { ChatMessageModel } from '@seahorse/domain';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InitialsPipe } from '../../../pipes/initials.pipe';
import { Subscription } from 'rxjs';
import { CardComponent } from '../../card/card.component';
import { LoadingIndicatorComponent } from '../../loading-indicator/loading-indicator.component';

@Component({
  selector: 'sh-chat-card',
  templateUrl: './chat-card.component.html',
  styles: [
    `
      .message-container {
        max-height: 400px;
        overflow-y: auto;
      }

      .message-sender {
        border: 1px solid #ced4da;
        background-color: antiquewhite;
        width: 45px;
      }

      .message-text {
        border: 1px solid #ced4da;
        display: inline-block;
      }

      .my-message-bg {
        background-color: #e6f2ff;
      }
    `,
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CardComponent,
    InitialsPipe,
    LoadingIndicatorComponent,
  ],
})
export class ChatCardComponent implements OnInit, OnDestroy {
  @ViewChild('observerTarget') observerTargetRef?: ElementRef;
  @ViewChildren('messages') messagesRef?: QueryList<ElementRef>;

  @Input() objectKeyField = '';
  @Input() objectKeyValue = '';

  message = '';
  messages: MessageModel[] = [];

  search = new MessagesSearchModel();
  pageIndex = 0;
  pageSize = 7;
  moreMessages = true;
  loading = false;

  private _intersectionObserver?: IntersectionObserver;
  private _subscription: Subscription = new Subscription();

  private _chatMessagesService = inject(ChatMessagesService);

  ngOnInit(): void {
    this.search.objectKeyField = this.objectKeyField;
    this.search.objectKeyValue = this.objectKeyValue;
    this.search.objectRelationship = 'chat_message';

    this.loadMessages();
  }

  ngOnDestroy(): void {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }
  }

  initializeObserver() {
    if (!this.moreMessages) return;

    this._intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.loadMoreMessages();
      });
    });

    setTimeout(() =>
      this._intersectionObserver?.observe(this.observerTargetRef?.nativeElement)
    );
  }

  loadMessages() {
    if (this.pageIndex === 0) {
      this.loading = true;
    }

    this._subscription.add(
      this._chatMessagesService
        .searchWithContentMaps(this.search, this.pageIndex, this.pageSize)
        .subscribe((result) => {
          if (result.result) {
            this.messages.unshift(...result.result.reverse());
          }

          this.moreMessages = result.count > this.messages.length;

          if (this.pageIndex === 0) {
            this.loading = false;
            this.scrollToBottom();
            this.initializeObserver();
          }

          if (this.pageIndex > 0 && this.moreMessages) {
            this.messagesRef?.first?.nativeElement.scrollIntoView();
          }

          this.pageIndex++;
        })
    );
  }

  loadMoreMessages() {
    if (this.moreMessages) {
      this.loadMessages();
    }
  }

  add() {
    const message: ChatMessageModel = {
      message: this.message,
      contentMapObject: {
        key: this.objectKeyField,
        value: this.objectKeyValue,
      },
    };

    this._subscription.add(
      this._chatMessagesService.addMessage(message).subscribe((result) => {
        this.message = '';

        if (result.status === ResultStatus.OK) {
          result.result.myMessage = true;
          this.messages.push(result.result);

          this.scrollToBottom();
        }
      })
    );
  }

  scrollToBottom() {
    setTimeout(() => {
      this.messagesRef?.last?.nativeElement.scrollIntoView();
    });
  }
}
